<i18n
  src="@/locales/components/views/packing_house/production/uniq_final_packing_list/show_container/tabs/pallets.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/package_group.yml" lang="yaml"></i18n>

<template>
  <div>
    <h1 class="page-title">
      <i class="fa-solid fa-pallet-boxes"></i><span class="space-icon"></span>
      {{ $t("labels.title") }}
    </h1>
    <div class="flex flex-row justify-end">
      <button
        type="button"
        @click.prevent="goToUploadPackingList(1)"
        class="btn-white"
      >
        <i class="fa-solid fa-cloud-upload"></i><span class="space-icon"></span
        >{{ $t("labels.upload_packing_list") }} 1
      </button>
      <span class="space-actions" />
      <button
        type="button"
        @click.prevent="goToUploadPackingList(2)"
        class="btn-white"
      >
        <i class="fa-solid fa-cloud-upload"></i><span class="space-icon"></span
        >{{ $t("labels.upload_packing_list") }} 2
      </button>
      <span class="space-actions" />
      <button
        type="button"
        @click.prevent="downloadVerticalFormatXlsx"
        class="btn-white"
      >
        <i class="fa-solid fa-download"></i><span class="space-icon"></span
        >{{ $t("labels.download_format") }} 1
      </button>
      <span class="space-actions" />
      <button
        type="button"
        @click.prevent="downloadHorizontalFormatXlsx"
        class="btn-white"
      >
        <i class="fa-solid fa-download"></i><span class="space-icon"></span
        >{{ $t("labels.download_format") }} 2
      </button>
      <span class="space-actions" />
      <tabs-download-buttons-packing-list
        :packing_house_process_id="packing_house_process_id"
        :container_code="this.container.container_code"
        :container_id="this.container_id"
      />
      <button
        type="button"
        v-if="can_add_package_group"
        @click.prevent="goToNew()"
        class="btn-white"
      >
        <i class="fa-solid fa-plus"></i><span class="space-icon"></span
        >{{ $t("labels.new_button") }}
      </button>
    </div>
    <div class="mt-8">
      <div class="inline-block min-w-full">
        <div class="grid grid-cols-2 gap-2">
            <template v-for="(package_group, idx) in packageGroupOrdered" :key="idx">
              <package-group-card
                v-if="package_group.id"
                :rounded_class="cardRoundedClass(package_group.col_position, package_group.row_position)"
                :package_group="package_group"
                :packing_house_process_id="packing_house_process_id"
                :show_organization="has_multiple_organizations"
                @destroy="remove($event)"
              />
              <empty-card
                v-else
                :row="package_group.row_position"
                :col="package_group.col_position"
                :rounded_class="cardRoundedClass(package_group.col_position, package_group.row_position)"
              />
            </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Requester from "@/views/packing_house/requesters/production/uniq_final_packing_list/upload_packing_list.js";
import PackageGroupCard from "@/components/views/packing_house/production/uniq_final_packing_list/show_container/tabs/pallets/PackageGroupCard.vue";
import EmptyCard from "@/components/views/packing_house/production/uniq_final_packing_list/show_container/tabs/pallets/EmptyCard.vue";
import TabsDownloadButtonsPackingList from "./DownloadButtonsPackingList.vue";

export default {
  name: "PalletsTab",
  components: {
    PackageGroupCard,
    EmptyCard,
    TabsDownloadButtonsPackingList
  },
  props: {
    container: {
      type: Object
    },
    package_groups: {
      type: Array,
      required: true
    }
  },
  emits: ["update:packages-groups"],
  data() {
    return {
      downloading_rep: false,
      pallet_charging: {}
    };
  },
  computed: {
    packageGroupOrdered: function () {
      let package_groups = [];
      for (let row = 1; row <= this.rows; row++) {
        for (let col = 1; col <= this.cols; col++) {
          const package_group = this.getPackageGroup(row, col);
          package_groups.push(package_group ? package_group : { row_position: row, col_position: col });
        }
      }
      return package_groups;
    },
    action_btn_class: function () {
      return "inline-flex justify-center py-2 px-4 border shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2";
    },
    packing_house_process_id: function () {
      return this.$router.currentRoute._value.params.packing_house_process_id;
    },
    card_width_class: function () {
      let cols = String(this.cols ?? 1);
      let width_class = `w-1/${cols}`;
      return `${width_class} lg:${width_class}`;
    },
    container_id: function () {
      return this.$router.currentRoute._value.params.container_id;
    },
    pallet_action_btn_class: function () {
      return `${this.action_btn_class} border-[#0d1f3c] text-[#0d1f3c] hover:text-white hover:bg-slate-800 focus:ring-[#5c7a2e] w-full lg:w-1/5`;
    },
    pallet_destroy_action_btn_class: function () {
      return `${this.action_btn_class} bg-red-700 border border-red-700 text-white hover:bg-red-700 focus:ring-red-500 w-full lg:w-1/5`;
    },
    any_package_group: function () {
      if (this.package_groups) return this.package_groups.length > 0;
      return false;
    },
    cols: function () {
      return this.container?.cols_package_groups ?? 1;
    },
    rows: function () {
      return this.container?.rows_package_groups ?? 1;
    },
    max_package_groups: function () {
      return this.container?.max_package_groups ?? 0;
    },
    can_add_package_group: function () {
      return this.package_groups.length < this.max_package_groups;
    },
    has_multiple_organizations: function() {
      return this.package_groups
        .map(pg => pg.packages).flat()
        .map(pkg => pkg.organization.name)
        .filter((value, idx, arr) => arr.indexOf(value) == idx)
        .length > 1
    }
  },
  methods: {
    cardRoundedClass: function (col, row) {
      let def_class =
        "relative group bg-white p-2 focus-within:ring-2 focus-within:ring-inset focus-within:ring-[#5c7a2e]";
      if (col == 1 && row == 1) def_class += " rounded-tl-lg";
      if (col == this.cols && row == 1) def_class += " sm:rounded-tr-lg";
      if (col == 1 && row == this.rows) def_class += " sm:rounded-bl-lg";
      if (col == this.cols && row == this.rows) def_class += " rounded-br-lg";
      return def_class;
    },
    changeCharging: function (package_group) {
      this.pallet_charging[package_group.id] =
        !this.pallet_charging[package_group.id];
    },
    getPackageGroup: function (row, col) {
      return this.package_groups.find(
        package_group =>
          package_group.row_position === row &&
          package_group.col_position === col
      );
    },
    goToNew: function () {
      this.$router.push({
        name: this.routeName("new_package_group"),
        params: {
          packing_house_process_id: this.packing_house_process_id,
          container_id: this.container_id
        }
      });
    },
    goToUploadPackingList: function (packing_list_format) {
      this.$router.push({
        name: this.routeName("upload_packing_list"),
        params: {
          packing_house_process_id: this.packing_house_process_id,
          container_id: this.container_id,
          packing_list_format: packing_list_format
        }
      });
    },
    remove: function (package_group) {
      this.$emit(
        "update:packages-groups",
        this.package_groups.filter(item => item.id !== package_group.id)
      );
    },
    replacePackageGroup: function (package_group, new_package_group) {
      this.$emit(
        "update:packages-groups",
        this.package_groups.map(item =>
          item.id === package_group.id ? new_package_group : item
        )
      );
    },
    downloadVerticalFormatXlsx: async function () {
      try {
        let response = await Requester.downloadVerticalFormatXlsx(
          this.packing_house_process_id,
          this.container_id
        );

        const file = response.data;
        this.downloadFile(file, "Formato Packing List 1.xlsx");
      } catch (error) {
        error;
      }
    },
    downloadHorizontalFormatXlsx: async function () {
      try {
        let response = await Requester.downloadHorizontalFormatXlsx(
          this.packing_house_process_id,
          this.container_id
        );

        const file = response.data;
        this.downloadFile(file, "Formato Packing List 2.xlsx");
      } catch (error) {
        console.log(error);
        error;
      }
    }
  }
};
</script>
